(ns client-portal.client.voice-insights.sessions.data
  (:require [shared.core.reactive :as re]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.voice-insights.tags.data :as tags.d]
            [client-portal.client.voice-insights.tag-data :refer [remove-internal-tags]]
            [shared.core.router :as router]
            [shared.core.util :as util]
            [shared.core.time :as time]
            [client-portal.client.router :as cp.router]
            [shared.core.components.recharts.util :as recharts.util]
            [client-portal.client.vi.components.tag :as tag]
            [clojure.string :as s]
            [shared.core.style :as style]))

(def root-path [:voice-insights :sessions])
(def list-path (conj root-path :list))
(def charts-list-path (conj root-path :charts-list))
(def charts-settings-path (conj root-path :charts))
(def utterances-path (conj root-path :utterances))
(def utterances-list-path (conj utterances-path :list))
(def count-path (conj root-path :count))
(def expanded-tags-path (conj root-path :expanded-tags))
(def drawer-open-path (conj root-path :drawer-open?))
(def selected-conversation-path (conj root-path :selected-conversation))
(def audio-current-time-path (conj root-path :audio-current-time))
(def filter-data-path (conj root-path :filter-data))
(def filter-popover-open?-path (conj root-path :filter-popover-open?))
(def filter-container-height-path (conj root-path :filter-container-height))
(def table-columns-path (conj root-path :table-columns))
(def view-mode-path (conj root-path :view-mode))
(def show-menu-item-tags-path (conj root-path :show-menu-item-tags?))
(def download-audio-path (conj root-path :download-audio))
(def admin-link-path (conj root-path :admin-link))
(def core-by-time-counts-path (conj root-path :core-by-time-counts))
(def enable-translations-path (conj root-path :enable-translations))
(def page-size 30)

(def default-filter-pair
  {:by "tags"
   :condition "include"
   :join "or"
   :value nil
   :children []})

(def default-filter-pair-child
  {:by nil
   :condition "include"
   :join "or"
   :value nil})

;; Config-driven numeric-comparison filters. Each entry is a filter `:by` whose value is a vector of
;; `{:op <token> :value <number-string>}` comparison entries. Register new numeric fields here (and add
;; matching column config on the server) — no bespoke code needed.
(def comparison-filter-options
  {"utterance-start" {:text "Utterance Start Time"
                      :unit "Seconds"
                      :unit-suffix "s"}})

(def comparison-operator-options
  [{:value "gt" :text ">"}
   {:value "gte" :text "≥"}
   {:value "lt" :text "<"}
   {:value "lte" :text "≤"}
   {:value "eq" :text "="}])

(def default-comparison-entry
  {:op (:value (first comparison-operator-options)) :value nil})

(defn comparison-filter? [by]
  (contains? comparison-filter-options by))

(defn conversation-by-id-path
  [{:keys [conversation-id]}]
  (conj root-path :conversation-by-id conversation-id))

(defn build-sessions-list-page-path [page]
  (conj list-path page))

(defn current-max-page [db]
  (some->> (get-in db list-path)
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn sessions-list-data-has-more? [db]
  (let [list-data (get-in db list-path)
        max-page (current-max-page db)]
    (some->> (get (util/when-not-loading list-data) max-page)
             (util/when-not-loading)
             (count)
             (<= page-size))))

(re/reg-sub :sessions/list-data-has-more?- sessions-list-data-has-more?)

(defn sessions-list-loading? [db]
  (let [list-data (get-in db list-path)]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub :sessions/list-loading?- sessions-list-loading?)

(defn sessions-list-next-page-loading? [db]
  (let [page (current-max-page db)]
    (and (pos? page) (util/loading? (get-in db (build-sessions-list-page-path page))))))

(re/reg-sub :sessions/list-next-page-loading?- sessions-list-next-page-loading?)

(defn list-next-page [db]
  (some-> (current-max-page db) (inc)))

(re/reg-sub :sessions/list-next-page- list-next-page)

(defn transf-sessions-resp [resp]
  (or (some->> resp :body (util/->kebab-case-keywords)
               (mapv #(-> %
                          (assoc :conversation-ts-raw (:conversation-ts %)
                                 :conversation-ts (time/reformat-date-str (:conversation-ts %) "M/d/yy h:mm A"))
                          (update :tags remove-internal-tags)
                          (update :utterance-tags remove-internal-tags))))
      []))

(defn base-query-args [db args]
  (let [filters (vi.d/filter-data-from-route db)]
    (merge (vi.d/base-query-args db args)
           (when (not-empty filters)
             {:filters filters}))))

(re/reg-auth0-loader
  :sessions/load-list-data
  (fn [[[_ {:keys [page]}]] _]
    (build-sessions-list-page-path (or page 0)))
  (fn [db [_ {:keys [page]} _]]
    (when (and (not (sessions-list-next-page-loading? db))
               (not (sessions-list-loading? db)))
      (when (concepts.d/current-concept-id db)
        (let [last-loaded (some-> (util/when-not-loading (get-in db list-path))
                                  (get (or (dec (or page 0)) 0))
                                  (last))]
          {:method :post
           :url "/api/vi/conversations/list"
           :json-params (merge (base-query-args db {:end-date (:conversation-ts-raw last-loaded)
                                                    :limit page-size})
                               {:end-date-conversation-id (:conversation-id last-loaded)})}))))
  :transf transf-sessions-resp
  :error-transf (fn [] []))

(re/reg-sub
  :sessions/list-
  (fn [db]
    (let [current-list (get-in db list-path)
          cur-page (if (util/loading? current-list) 0 (current-max-page db))]
      (if (nil? current-list)
        (re/dispatch-load (conj [:sessions/load-list-data
                                 [nil {:page (or (some-> cur-page (inc)) 0)}]]))
        current-list))))

(defn update-intent-resolution [d]
  (some->> d
           (mapv (fn [r]
                   (assoc r :value (tag/tag->display-name {:value (:value r)})
                            :child-value (tag/tag->display-name {:value (:child-value r)}))))))

(defn transf-charts-data [{:keys [by-session by-tag session-count intent-resolution improvement-recommendations
                                  intent-summary-by-sentiment intent-sentiment-flow]}]
  (let [round-session-counts #(mapv (fn [i]
                                      (assoc i :count (util/to-precision-num (:count i) :precision 1)))
                                    %)
        by-session (recharts.util/group-and-format-time-grouping-data
                     {:data (mapv (fn [v]
                                    (assoc v :tag-counts (round-session-counts (:tag-counts v))))
                                  by-session)
                      :forced-time-key :name})
        intent-resolution (update-intent-resolution intent-resolution)
        intent-resolution-by-value (some->> intent-resolution (group-by :value))]
    (cond-> {:by-tag (recharts.util/group-and-format-time-grouping-data {:data by-tag
                                                                         :forced-time-key :name})
             :by-session by-session
             :session-count (recharts.util/group-and-format-time-grouping-data {:data session-count})
             :intent-sentiment-flow intent-sentiment-flow}

            intent-resolution-by-value
            (assoc :intent-resolution-sankey
                   (let [sorted-intents (->> intent-resolution-by-value
                                             (mapv (fn [[k vals]]
                                                     {:name k
                                                      :value (apply + (map :count vals))
                                                      :vals vals}))
                                             (sort-by :value)
                                             (reverse))
                         top-10-keys (->> sorted-intents
                                          (take 10)
                                          (map :name)
                                          (into #{}))
                         all-pairs (->> intent-resolution-by-value
                                        (mapcat (fn [[intent-name vals]]
                                                  (->> vals
                                                       (group-by :child-value)
                                                       (mapv (fn [[resolution-name resolution-vals]]
                                                               {:intent intent-name
                                                                :resolution resolution-name
                                                                :count (apply + (map :count resolution-vals))})))))
                                        (vec))
                         generate-sankey-data (fn [pairs]
                                                (let [intents (->> pairs
                                                                   (map :intent)
                                                                   (distinct)
                                                                   (sort))
                                                      resolutions (->> pairs
                                                                       (map :resolution)
                                                                       (distinct)
                                                                       (sort))
                                                      intent-node-lookups (->> intents
                                                                               (map-indexed (fn [idx name]
                                                                                              [name idx]))
                                                                               (into {}))
                                                      intent-nodes (mapv (fn [[name _]]
                                                                           {:name name
                                                                            :isSource true
                                                                            :color (recharts.util/color-from-string name)
                                                                            :width 10
                                                                            :stroke (style/alc-color "Background/card_hex")
                                                                            :strokeWidth 1
                                                                            :opacity 1.0})
                                                                         (sort-by second intent-node-lookups))
                                                      resolution-node-lookups (->> resolutions
                                                                                   (map-indexed (fn [idx name]
                                                                                                  [name (+ idx (count intents))]))
                                                                                   (into {}))
                                                      resolution-nodes (mapv (fn [[name _]]
                                                                               {:name name
                                                                                :isSource false
                                                                                :color (recharts.util/color-from-string name)
                                                                                :width 10
                                                                                :stroke (style/alc-color "Background/card_hex")
                                                                                :strokeWidth 1
                                                                                :opacity 1.0})
                                                                             (sort-by first resolution-node-lookups))
                                                      nodes (vec (concat intent-nodes resolution-nodes))
                                                      links (mapv (fn [{:keys [intent resolution count]}]
                                                                    {:source (get intent-node-lookups intent)
                                                                     :target (get resolution-node-lookups resolution)
                                                                     :value count})
                                                                  pairs)]
                                                  {:nodes nodes
                                                   :links links}))]
                     {:all (generate-sankey-data all-pairs)
                      :top-10 (generate-sankey-data (filterv #(contains? top-10-keys (:intent %)) all-pairs))
                      :top-10-keys top-10-keys}))

            intent-resolution
            (assoc :intent-resolution-rate (->> intent-resolution
                                                (group-by :child-value)
                                                (mapv (fn [[k vals]]
                                                        {:name k
                                                         :value (apply + (map :count vals))
                                                         :fill (recharts.util/color-from-string k)}))))

            improvement-recommendations
            (assoc :improvement-recommendations (mapv (fn [{:keys [value count] :as r}]
                                                        {:name (tag/tag->display-name {:value value})
                                                         :value count
                                                         :fill (recharts.util/color-from-string value)
                                                         :raw r})
                                                      improvement-recommendations))

            intent-summary-by-sentiment
            (assoc :intent-summary-by-sentiment (mapv (fn [r]
                                                        (assoc r :value (tag/tag->display-name {:value (:value r)})
                                                                 :child-value (tag/tag->display-name {:value (:child-value r)})
                                                                 :raw r))
                                                      intent-summary-by-sentiment)))))

;; Loader for charts view - fetches all data without limit
(re/reg-auth0-loader
  :sessions/load-charts-data
  charts-list-path
  (fn [db _]
    (let [cur-tag-names (tags.d/current-tag-names-list-set db)]
      (when (and (concepts.d/current-concept-id db)
                 (not (util/loading? cur-tag-names))
                 (not-empty cur-tag-names))
        {:method :post
         :url "/api/vi/conversations/charts"
         :json-params (assoc (base-query-args db {})
                        :data-keys (cond-> ["session-count" "by-session" "by-tag"]
                                           (contains? cur-tag-names "intent-summary")
                                           (conj "intent-resolution"
                                                 "intent-summary-by-sentiment"
                                                 "intent-sentiment-flow")

                                           (contains? cur-tag-names "improvement-recommendation")
                                           (conj "improvement-recommendations")))})))
  :transf #(or (some-> % :body (util/->kebab-case-keywords) (transf-charts-data)) [])
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :sessions/charts-list-
  charts-list-path
  :sessions/load-charts-data)

(re/reg-auth0-loader
  :sessions/load-count
  count-path
  (fn [db]
    (when (concepts.d/current-concept-id db)
      {:method :post
       :url "/api/vi/conversations/count"
       :json-params (base-query-args db {})}))
  :transf #(-> % :body (util/->kebab-case-keywords) :count)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :sessions/count-
  count-path
  :sessions/load-count)

(defn list->flattened-list [list-data]
  (some->> (util/when-not-loading list-data)
           (into (sorted-map))
           (filterv #(not (util/loading? (last %))))
           (mapv last)
           (flatten)
           (vec)))

(defn db->flattened-list [db]
  (-> (get-in db list-path)
      (list->flattened-list)))

(re/reg-sub
  :sessions/list-flattened-
  :<- [:sessions/list-]
  list->flattened-list)

(re/reg-event-db
  :sessions/reload-conversations-list re/middleware
  (fn [db]
    (-> db
        (assoc-in list-path nil)
        (assoc-in charts-list-path nil)
        (assoc-in utterances-list-path nil)
        (assoc-in count-path nil))))

(defn conversation->conv-qp [{:keys [conversation-id dataset-id namespace conversation-ts start]}]
  (try
    (some-> (when conversation-id
              {:conversation-id conversation-id
               :dataset-id dataset-id
               :namespace namespace
               :conversation-ts conversation-ts
               :start start})
            (util/remove-nil-from-map)
            (util/map->json-string)
            (util/base-64-encode))
    (catch :default _
      nil)))

(defn conv-qp->conversation [conv-qp]
  (try
    (some-> conv-qp
            (util/base-64-decode)
            (util/json-string->map))
    (catch :default _
      nil)))

(defn transf-conversation [convo]
  (let [translations-by-start (some->> convo
                                       :utterance-tags
                                       (filter #(= (:name %) "internal/translation"))
                                       (mapv (fn [{:keys [start] :as r}]
                                               [start (update r :description vi.d/clean-bad-words)]))
                                       (into {}))]
    (-> convo
        (update :tags #(->> %
                            (remove-internal-tags)
                            (mapv (fn [t] (update t :description vi.d/clean-bad-words)))))
        (update :utterance-tags #(->> %
                                      (remove-internal-tags)
                                      (mapv (fn [t] (update t :description vi.d/clean-bad-words)))))
        (update :utterances #(mapv (fn [{:keys [transcript] :as u}]
                                     (assoc u :transcript (vi.d/clean-bad-words transcript)))
                                   %))

        (assoc :utterance-translations-by-start translations-by-start))))

(re/reg-auth0-loader
  :sessions/conversation-by-id
  (fn [[[_ args]]]
    (conversation-by-id-path args))
  (fn [db [_ args]]
    (when-let [conversation-id (:conversation-id args)]
      {:method :get
       :url "/api/vi/conversation"
       :query-params {:conversation-id conversation-id
                      :concept-id (concepts.d/current-concept-id db)
                      :namespace (or (:namespace args) (:ns (router/current-query-params db)))
                      :dataset-id (:dataset-id args)}}))
  :transf (fn [resp]
            (or (some-> resp
                        :body
                        (util/->kebab-case-keywords)
                        (transf-conversation))
                []))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :sessions/conversation-by-id-
  (fn [_ args]
    (conversation-by-id-path args))
  :sessions/conversation-by-id)

(re/reg-sub
  :sessions/expanded-tags-
  (fn [db]
    (get-in db expanded-tags-path)))

(re/reg-event-db
  :sessions/toggle-expanded-tag re/middleware
  (fn [db [tag]]
    (let [expanded-tags (get-in db expanded-tags-path #{})]
      (if (contains? expanded-tags tag)
        (assoc-in db expanded-tags-path (disj expanded-tags tag))
        (assoc-in db expanded-tags-path (conj expanded-tags tag))))))

(re/reg-sub
  :sessions/selected-conversation-
  (fn [db]
    (get-in db selected-conversation-path nil)))

(defn go-with-merged-conv [conv]
  (re/dispatch [:router/go-with-merged-query-params {:conv (conversation->conv-qp conv)}]))

(re/reg-event-db
  :sessions/toggle-drawer-open re/middleware
  (fn [db [args]]
    (let [currently-open? (some-> (get-in db [:layout :active-panel-right]) (= :sessions/conversation-panel))
          open? (if (nil? (:open? args))
                  (not currently-open?)
                  (:open? args))
          args (dissoc args :open?)
          cur-convo (get-in db selected-conversation-path)
          args (if (and (= (:conversation-id cur-convo) (:conversation-id args))
                        (:convo-idx cur-convo)
                        (not (:convo-idx args)))
                 (assoc args :convo-idx (:convo-idx cur-convo))
                 args)]
      (when open?
        (re/dispatch [:voice-insights/close-sidebar]))
      (if open?
        (when-not currently-open?
          (re/dispatch [:layout/show-panel-right [:sessions/conversation-panel args]]))
        (re/dispatch [:layout/hide-panel-right]))
      (go-with-merged-conv args)
      (assoc-in db selected-conversation-path (when open? args)))))

(re/reg-event-db
  :sessions/move-conversation re/middleware
  (fn [db [direction]]
    (let [current-conversation (get-in db selected-conversation-path)
          conversations (db->flattened-list db)
          current-index (:convo-idx current-conversation)
          first-convo? (= current-index 0)
          last-convo? (>= current-index (dec (count conversations)))
          next-index (case direction
                       :next (when-not last-convo?
                               (inc current-index))
                       :prev (when-not first-convo?
                               (dec current-index)))
          next-conversation (get conversations next-index)]
      (when (and (>= next-index (dec (count conversations))) (= direction :next))
        (re/dispatch [:sessions/load-list-data [nil {:page (list-next-page db)}]]))
      (if next-conversation
        (let [conv {:conversation-id (:conversation-id next-conversation)
                    :dataset-id (:dataset-id next-conversation)
                    :conversation-ts (:conversation-ts next-conversation)
                    :location-id (:location-id next-conversation)
                    :conversation-tags (:conversation-tags next-conversation)}]
          (go-with-merged-conv conv)
          (assoc-in db selected-conversation-path (assoc conv :convo-idx next-index)))
        db))))

(re/reg-sub
  :sessions/audio-current-time-
  (fn [db _]
    (get-in db audio-current-time-path 0)))

(re/reg-event-db
  :sessions/set-audio-current-time re/middleware
  (fn [db [current-time]]
    (assoc-in db audio-current-time-path current-time)))

(defn filter-data [db]
  (let [fd (get-in db filter-data-path)]
    (if (empty? fd)
      [default-filter-pair]
      fd)))

(defn find-valid-filters [filters]
  (filter #(and (:by %) (:condition %) (seq (:value %))) filters))

(defn populate-default-filter-if-empty [filters]
  (if (empty? filters)
    [default-filter-pair]
    filters))

(defn populate-default-filter-child-if-empty [children]
  (if (empty? children)
    [default-filter-pair-child]
    children))

(defn normalize-loaded-filter
  "`filter-data-from-route` returns value entries as string-keyed maps. Comparison entries
   in the editor use keyword keys (`{:op _ :value _}`), so keywordize them back on load."
  [f]
  (if (comparison-filter? (:by f))
    (update f :value #(mapv util/->kebab-case-keywords %))
    f))

(re/reg-event-db
  :sessions/initialize-filters re/middleware
  (fn [db]
    (-> (core.d/initialize-common-filters db)
        (assoc-in filter-data-path (populate-default-filter-if-empty
                                     (mapv normalize-loaded-filter (vi.d/filter-data-from-route db)))))))

(re/reg-event-db
  :sessions/add-filter-pair re/middleware
  (fn [db]
    (assoc-in db filter-data-path (vec (conj (filter-data db) default-filter-pair)))))

(re/reg-event-db
  :sessions/remove-filter-pair re/middleware
  (fn [db [index]]
    (let [current-filters (filter-data db)
          updated-filters (vec (concat (take index current-filters)
                                       (drop (inc index) current-filters)))]
      (assoc-in db filter-data-path (populate-default-filter-if-empty updated-filters)))))

(re/reg-event-db
  :sessions/update-filter-pair re/middleware
  (fn [db [index by value]]
    (assoc-in db filter-data-path (vec (update (filter-data db) index #(assoc % by value))))))

(re/reg-event-db
  :sessions/add-filter-pair-child re/middleware
  (fn [db [parent-index]]
    (assoc-in db (conj filter-data-path parent-index :children) (vec (conj (-> (get (filter-data db) parent-index)
                                                                               :children)
                                                                           default-filter-pair-child)))))

(re/reg-event-db
  :sessions/remove-filter-pair-child re/middleware
  (fn [db [parent-index index]]
    (let [current-children (-> (get (filter-data db) parent-index) :children)
          updated-children (vec (concat (take index current-children)
                                        (drop (inc index) current-children)))]
      (assoc-in db (conj filter-data-path parent-index :children)
                (populate-default-filter-child-if-empty updated-children)))))

(re/reg-event-db
  :sessions/update-filter-pair-child re/middleware
  (fn [db [parent-index index by value]]
    (let [path (conj filter-data-path parent-index :children index)]
      (assoc-in db path (merge (or (get-in db path) {}) {by value})))))

(re/reg-event-db
  :sessions/update-comparison-entry re/middleware
  (fn [db [index entry-index key value]]
    (let [path (conj filter-data-path index :value entry-index)]
      (assoc-in db path (merge (or (get-in db path) {}) {key value})))))

(defn valid-comparison-entry? [entry]
  (and (:op entry) (some? (util/parse-int (:value entry)))))

(defn normalize-comparison-filter [f]
  (if (comparison-filter? (:by f))
    (update f :value #(filterv valid-comparison-entry? %))
    f))

(defn filters->query-param [filters]
  (let [filters (mapv normalize-comparison-filter filters)
        valid-filters (filter #(and (:by %) (:condition %) (seq (:value %)))
                              filters)
        valid-filters (when (not-empty valid-filters)
                        (->> valid-filters
                             (mapv (fn [f]
                                     (assoc f :children (filterv #(and (:by %) (:condition %) (seq (:value %))
                                                                       (not-empty (:value %)))
                                                                 (:children f)))))))]
    (some-> valid-filters
            (util/map->json-string-min)
            (util/base-64-encode))))

(re/reg-sub
  :sessions/filter-data-query-param-
  (fn [db]
    (some-> (filter-data db)
            (filters->query-param))))

(re/reg-sub
  :sessions/applied-filters-
  (fn [db]
    (vi.d/filter-data-from-route db)))

(re/reg-event-db
  :sessions/expand-column-by-id re/middleware
  (fn [db [column-id]]
    (let [column-indexes-path (conj table-columns-path :expanded-column-indexes)]
      (if-let [column-idx (some-> (js/document.querySelector (str "[data-column-id='" column-id "']"))
                                  (.getAttribute "data-column-index")
                                  (util/parse-int))]
        (assoc-in db column-indexes-path (conj (get-in db column-indexes-path #{}) column-idx))
        db))))

(re/reg-event-db
  :sessions/apply-filters re/middleware
  (fn [db]
    (let [cur-fd (:fd (router/current-query-params db))
          db-filter-data (filter-data db)
          new-fd (filters->query-param db-filter-data)
          tags (->> db-filter-data
                    (filter #(= (:by %) "tags"))
                    (map :value)
                    (flatten)
                    (map #(get % "name"))
                    (filter identity)
                    (distinct)
                    (into #{}))]
      (when (not-empty tags)
        (when (some #(contains? tags %) ["size-upsell" "combo-upsell" "add-on-upsell"])
          (re/dispatch [:sessions/expand-column-by-id "marketing"]))
        (when (some #(contains? tags %) ["start" "pace"])
          (re/dispatch [:sessions/expand-column-by-id "efficiency"]))
        (when (some #(contains? tags %) ["greeting" "sentiment"])
          (re/dispatch [:sessions/expand-column-by-id "satisfaction"]))
        (when (some #(contains? tags %) ["outcome" "ai-outcome"])
          (re/dispatch [:sessions/expand-column-by-id "outcome"])))
      (when (not= cur-fd new-fd)
        (re/dispatch [:router/go-with-merged-query-params
                      {:fd new-fd}])
        (re/dispatch [:sessions/reload-conversations-list]))
      db)))

(re/reg-event-db
  :sessions/clear-filters re/middleware
  (fn [db]
    (re/dispatch [:router/go-with-merged-query-params {:fd nil}])
    (-> db
        (assoc-in list-path nil)
        (assoc-in charts-list-path nil)
        (assoc-in utterances-list-path nil)
        (assoc-in count-path nil)
        (assoc-in filter-data-path [default-filter-pair]))))

(re/reg-event-db
  :sessions/delete-filter-data-index re/middleware
  (fn [db [index]]
    (assoc-in db filter-data-path (populate-default-filter-if-empty (util/vec-remove (filter-data db) index)))))

(re/reg-sub :sessions/filter-data- filter-data)

(re/reg-sub
  :sessions/valid-filter-data-
  :<- [:sessions/filter-data-]
  (fn [filters]
    (find-valid-filters filters)))

(defn sharable-conversation-url [concept-id conversation crew-type conv-namespace]
  (str js/window.location.origin
       (cp.router/route-details->href :vi/sessions
                                      nil
                                      (cond-> {:cid concept-id
                                               :ct crew-type
                                               :conv (conversation->conv-qp conversation)}
                                              conv-namespace
                                              (assoc :ns conv-namespace)))))

(re/reg-event-db
  :sessions/open-filter-popover re/middleware
  (fn [db]
    (assoc-in db filter-popover-open?-path true)))

(re/reg-event-db
  :sessions/close-filter-popover re/middleware
  (fn [db]
    (assoc-in db filter-popover-open?-path false)))

(re/reg-event-db
  :sessions/toggle-filter-popover re/middleware
  (fn [db [open?]]
    (assoc-in db filter-popover-open?-path (boolean open?))))

(re/reg-sub
  :sessions/filter-popover-open?-
  (fn [db]
    (boolean (get-in db filter-popover-open?-path))))

(re/reg-sub
  :sessions/filter-container-height-
  (fn [db]
    (get-in db filter-container-height-path)))

(re/reg-event-db
  :sessions/set-filter-container-height re/middleware
  (fn [db [h]]
    (assoc-in db filter-container-height-path h)))

(re/reg-event-fx
  :sessions/initialize re/middleware
  (fn [{:keys [db]}]
    (re/dispatch [:sessions/initialize-filters])
    (re/dispatch [:sessions/reload-conversations-list])
    {:db (assoc-in db selected-conversation-path nil)
     :forward-events {:register :vi-sessions-initialization-listener
                      :events #{:concepts/select :locations/select :core/select-crew-type
                                :core/select-timeframe :locations/clear-selected
                                :core/timeframe-date-range-changed}
                      :dispatch-to [:sessions/reload-conversations-list]}}))

(defn view-mode [db]
  (or (:svm (router/current-query-params db)) "sessions"))

(re/reg-sub :sessions/view-mode- view-mode)

(re/reg-event-db
  :sessions/select-view-mode re/middleware
  (fn [db [mode]]
    (re/dispatch [:router/go-with-merged-query-params {:svm mode}])
    db))

(defn show-menu-item-tags? [db]
  (get-in db show-menu-item-tags-path false))

(re/reg-sub :sessions/show-menu-item-tags?- show-menu-item-tags?)

(re/reg-event-db
  :sessions/toggle-menu-item-tags re/middleware
  (fn [db]
    (assoc-in db show-menu-item-tags-path (not (show-menu-item-tags? db)))))

(def default-filter-value->session-qp
  "Maps a report `defaultFilterValues` key to the query-param key it seeds on the
   report's route. Used to fill only the params the current route doesn't already
   carry."
  {"crewType" :ct
   "locationId" :lid
   "conceptId" :cid
   "startDate" :start
   "endDate" :end
   "namespace" :ns
   "datasetId" :dsid
   "dataset" :ds})

(defn default-filter-values->session-qps
  "A report's `defaultFilterValues` as sessions-page query params. A supplied
   start/end date also implies the custom-timeframe marker (`:tf -1`). Merge these
   UNDER the current route's params so live/UI selections win."
  [dfv]
  (let [qps (into {}
                  (keep (fn [[k v]]
                          (when-let [qp (and (some? v) (default-filter-value->session-qp (name k)))]
                            [qp v])))
                  dfv)]
    (cond-> qps
            (or (:start qps) (:end qps)) (assoc :tf -1))))

(re/reg-sub
  :sessions/relevant-query-params-
  (fn [db]
    (util/remove-nil-from-map (select-keys (router/current-query-params db) [:cid :lid :ct :tf :ns :dsid :start :end :ds]))))

(re/reg-auth0-loader
  :sessions/download-conversation-audio
  download-audio-path
  (fn [db [args]]
    (when-let [conversation-id (:conversation-id args)]
      {:method :get
       :url "/api/vi/conversation/audio"
       :response-type :blob
       :query-params {:conversation-id conversation-id
                      :concept-id (concepts.d/current-concept-id db)
                      :namespace (or (:namespace args) (:ns (router/current-query-params db)))
                      :dataset-id (:dataset-id args)}}))
  :transf (fn [{:keys [success body headers]} db]
            (if success
              (let [filename (or (some-> (get headers "content-disposition")
                                         (s/split "filename=")
                                         (last)
                                         (s/replace "\"" ""))
                                 "audio.wav")]
                (util/download-blob {:blob body :download-name filename}))
              (re/dispatch [:toasts/add-error {:msg "Failed to download audio"
                                               :duration 5}])))
  :error-transf (fn [_] nil))

(re/reg-event-db
  :sessions/download-active-session-audio re/middleware
  (fn [db]
    (when-let [convo (get-in db selected-conversation-path)]
      (re/dispatch [:sessions/download-conversation-audio [convo]]))
    (assoc-in db download-audio-path nil)))

(re/auth0-http-request
  :sessions/open-in-admin
  admin-link-path
  (fn [db {:keys [conversation-id audio-source dataset-id namespace]}]
    (when conversation-id
      {:method :get
       :url "/api/vi/conversation/admin-link"
       :query-params (util/remove-nil-from-map
                       {:conversation-id conversation-id
                        :audio-source audio-source
                        :concept-id (concepts.d/current-concept-id db)
                        :namespace (or namespace (:ns (router/current-query-params db)))
                        :dataset-id dataset-id})}))
  :on-success (fn [db [resp] {:keys [event-args]}]
                (let [body (or (:body resp) {})
                      admin-url (or (:admin-url body)
                                    (:adminUrl body))
                      target-window (:target-window event-args)]
                  (cond
                    admin-url
                    (do
                      (if target-window
                        (set! (.-location target-window) admin-url)
                        (js/window.open admin-url "_blank"))
                      db)

                    :else
                    (do
                      (when target-window (.close target-window))
                      (re/dispatch [:toasts/add-error {:msg "Admin conversation not found for this session"
                                                       :duration 5}])
                      db))))
  :on-fail (fn [db resp {:keys [event-args]}]
             (when-let [w (:target-window event-args)]
               (.close w))
             (let [status (:status resp)
                   body (or (:body resp) {})
                   msg (or (:error body)
                           (when (= status 404)
                             "Admin conversation not found for this session")
                           (when (= status 403)
                             "Not allowed to open this conversation in Admin")
                           "Unable to open in Admin")]
               (re/dispatch [:toasts/add-error {:msg msg :duration 5}]))
             db))

(re/reg-event-db
  :sessions/open-in-admin-click re/middleware
  (fn [db [args]]
    (let [target-window (js/window.open "about:blank" "_blank")]
      (if target-window
        (re/dispatch [:sessions/open-in-admin (assoc args :target-window target-window)])
        (re/dispatch [:toasts/add-error {:msg "Popup blocked — allow popups for this site to open Admin"
                                         :duration 5}])))
    db))

(re/reg-auth0-loader
  :voice-insights/load-core-session-by-time-counts
  core-by-time-counts-path
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/conversations/count/by-time"
     :json-params (vi.d/base-query-args db args)})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         {}))
  :error-transf (fn [] {}))

(re/reg-auth0-load-sub
  :voice-insights/core-session-by-time-counts-
  core-by-time-counts-path
  :voice-insights/load-core-session-by-time-counts)

(re/reg-event-db
  :voice-insights/reload-core-session-by-time-counts
  (fn [db]
    (assoc-in db core-by-time-counts-path nil)))

(re/reg-sub
  :voice-insights/core-session-by-time-counts-keyed-
  :<- [:voice-insights/core-session-by-time-counts-]
  (fn [core-counts]
    (if (util/loading? core-counts)
      core-counts
      (->> core-counts
           (group-by :time-grouping)
           (mapv (fn [[k vals]]
                   [k (util/group-by-distinct :time-value vals)]))
           (into {})))))

(defn jump-to-audio [jump-to]
  (when-let [audio-el (.getElementById js/document "session-audio-player")]
    (.play audio-el)
    (set! (.-currentTime audio-el) (/ jump-to 1000))))

(re/reg-event-db
  :voice-insights/active-session-jump-to-time re/middleware
  (fn [db [{:keys [jump-to scroll-to? play-audio?]
            :or {play-audio? true}}]]
    (if scroll-to?
      (do
        (when play-audio?
          (util/wait-for-scroll-end (js/document.getElementById "session-drawer-scroll-container")
                                    #(jump-to-audio jump-to)))
        (some-> (js/document.querySelector (str "[data-start-ms='" jump-to "']"))
                (util/smooth-scroll-to-view)))
      (jump-to-audio jump-to))
    (assoc-in db (conj root-path :search-transcript) nil)))
